export type WearablePathTrack =
  | "creator"
  | "xr"
  | "readiness"
  | "recovery"
  | "general";

export type WearablePathSource =
  | "bridge"
  | "baseline"
  | "onboarding"
  | "fallback"
  | "override-pinned"
  | "override-dismissed";

export type WearablePathSurfaceMode = "guided-studio" | "omnistudio";

export type WearableWorkspaceBias =
  | "creator"
  | "xr"
  | "guided"
  | "calm"
  | "standard";

export type WearableProfileBridgeLike = Partial<{
  preferredTrack: string | null;
  modeBias: string | null;
  recommendedSurfaceIds: string[];
  utilityBias: string[];
  identitySignals: string[];
  xrDefaults: Record<string, unknown> | null;
  creatorDefaults: Record<string, unknown> | null;
  profileSummary: string[] | string | null;
}>;

export type WearableIdentityBaselineLike = Partial<{
  preferredTrack: string | null;
}>;

export type PathAwareSortableItem = {
  id: string;
  kind?: string | null;
  title?: string | null;
  tags?: string[] | null;
  surfaceId?: string | null;
};

export type WearablePathDefaultsInput = {
  bridge?: WearableProfileBridgeLike | null;
  baseline?: WearableIdentityBaselineLike | null;
  onboardingTrack?: string | null;
};

export type WearablePathEntryEmphasis = {
  eyebrow: string;
  title: string;
  body: string;
};

export type WearablePathDefaults = {
  track: WearablePathTrack;
  source: WearablePathSource;
  isDurable: boolean;
  continuityChips: string[];
  advisory: {
    summary: string;
    sourceLabel: string;
    overrideLabel: string;
  };
  guidedStudio: {
    entryEmphasis: WearablePathEntryEmphasis;
    entryPriorityTokens: string[];
    workspaceBias: WearableWorkspaceBias;
    surfacePriorityLabel: string;
  };
  omniStudio: {
    entryEmphasis: WearablePathEntryEmphasis;
    orderedSurfaceIds: string[];
    workspaceBias: WearableWorkspaceBias;
    surfacePriorityLabel: string;
    emphasisTokens: string[];
  };
};

const VALID_TRACKS: WearablePathTrack[] = [
  "creator",
  "xr",
  "readiness",
  "recovery",
  "general",
];

const TRACK_LABELS: Record<WearablePathTrack, string> = {
  creator: "Creator",
  xr: "XR",
  readiness: "Readiness",
  recovery: "Recovery",
  general: "General",
};

const SOURCE_LABELS: Record<WearablePathSource, string> = {
  bridge: "Using durable wearable profile bias",
  baseline: "Using durable wearable baseline bias",
  onboarding: "Using onboarding continuity fallback",
  fallback: "Using neutral default continuity",
  "override-pinned": "Using your pinned entry preference",
  "override-dismissed": "Path-aware guidance dismissed for this surface",
};

type TrackConfig = {
  summary: string;
  guidedStudio: {
    entryEmphasis: WearablePathEntryEmphasis;
    entryPriorityTokens: string[];
    workspaceBias: WearableWorkspaceBias;
    surfacePriorityLabel: string;
    sortTokens: string[];
  };
  omniStudio: {
    entryEmphasis: WearablePathEntryEmphasis;
    workspaceBias: WearableWorkspaceBias;
    surfacePriorityLabel: string;
    emphasisTokens: string[];
    sortTokens: string[];
  };
};

const TRACK_CONFIGS: Record<WearablePathTrack, TrackConfig> = {
  creator: {
    summary:
      "Bias entry toward creator-facing setup, composition flow, and creator-oriented utilities. Advisory only.",
    guidedStudio: {
      entryEmphasis: {
        eyebrow: "Creator-biased entry",
        title: "Start with creator-oriented first steps",
        body: "Lead with creation, composition, capture-adjacent setup, and creator-facing workspace choices.",
      },
      entryPriorityTokens: ["creator", "compose", "build", "story", "edit"],
      workspaceBias: "creator",
      surfacePriorityLabel: "Creator-first surface priority",
      sortTokens: ["creator", "compose", "build", "story", "studio", "edit"],
    },
    omniStudio: {
      entryEmphasis: {
        eyebrow: "Creator workspace emphasis",
        title: "Prioritize creator-facing studio entry",
        body: "Bring creator-oriented launch surfaces forward without mutating editorial or compile authority.",
      },
      workspaceBias: "creator",
      surfacePriorityLabel: "Creator-weighted studio ordering",
      emphasisTokens: ["creator", "compose", "scene", "studio", "editorial"],
      sortTokens: ["creator", "compose", "studio", "scene", "edit", "build"],
    },
  },
  xr: {
    summary:
      "Bias entry toward spatial / immersive setup, XR workspace framing, and XR-relevant utilities. Advisory only.",
    guidedStudio: {
      entryEmphasis: {
        eyebrow: "XR-biased entry",
        title: "Start inside XR-aware context",
        body: "Lead with spatial framing, immersive setup, world-aware tooling, and XR-relevant first actions.",
      },
      entryPriorityTokens: ["xr", "spatial", "world", "immersive", "capture"],
      workspaceBias: "xr",
      surfacePriorityLabel: "XR-first surface priority",
      sortTokens: ["xr", "spatial", "world", "immersive", "capture", "scene"],
    },
    omniStudio: {
      entryEmphasis: {
        eyebrow: "XR workspace emphasis",
        title: "Prioritize spatial and immersive studio entry",
        body: "Bring XR-facing workspace surfaces forward while keeping OmniStudio advisory and overridable.",
      },
      workspaceBias: "xr",
      surfacePriorityLabel: "XR-weighted studio ordering",
      emphasisTokens: ["xr", "spatial", "world", "scene", "immersive"],
      sortTokens: ["xr", "spatial", "world", "immersive", "scene", "capture"],
    },
  },
  readiness: {
    summary:
      "Bias entry toward readiness-oriented summaries, signal-aware framing, and lower-friction launch decisions. Advisory only.",
    guidedStudio: {
      entryEmphasis: {
        eyebrow: "Readiness-biased entry",
        title: "Start with readiness context first",
        body: "Lead with signal-aware summaries, launch clarity, and quick-entry surfaces before deeper workspace branching.",
      },
      entryPriorityTokens: ["readiness", "signal", "summary", "start", "focus"],
      workspaceBias: "guided",
      surfacePriorityLabel: "Readiness-first surface priority",
      sortTokens: ["readiness", "signal", "summary", "focus", "start", "home"],
    },
    omniStudio: {
      entryEmphasis: {
        eyebrow: "Readiness workspace emphasis",
        title: "Prioritize clarity-first studio entry",
        body: "Bring guidance, summary, and lower-friction launch surfaces forward before secondary studio surfaces.",
      },
      workspaceBias: "guided",
      surfacePriorityLabel: "Readiness-weighted studio ordering",
      emphasisTokens: ["readiness", "summary", "focus", "guided", "home"],
      sortTokens: ["readiness", "summary", "focus", "guided", "home", "signal"],
    },
  },
  recovery: {
    summary:
      "Bias entry toward recovery-oriented guidance, calmer launch surfaces, and lower-friction first actions. Advisory only.",
    guidedStudio: {
      entryEmphasis: {
        eyebrow: "Recovery-biased entry",
        title: "Start with calmer guided first steps",
        body: "Lead with recovery-framed guidance, reduced-friction actions, and supportive entry surfaces first.",
      },
      entryPriorityTokens: ["recovery", "restore", "guided", "calm", "summary"],
      workspaceBias: "calm",
      surfacePriorityLabel: "Recovery-first surface priority",
      sortTokens: ["recovery", "restore", "guided", "calm", "summary", "home"],
    },
    omniStudio: {
      entryEmphasis: {
        eyebrow: "Recovery workspace emphasis",
        title: "Prioritize lower-friction studio entry",
        body: "Bring calmer, guided, recovery-aware surfaces forward while keeping all defaults advisory and reversible.",
      },
      workspaceBias: "calm",
      surfacePriorityLabel: "Recovery-weighted studio ordering",
      emphasisTokens: ["recovery", "guided", "calm", "summary", "home"],
      sortTokens: ["recovery", "guided", "calm", "summary", "restore", "home"],
    },
  },
  general: {
    summary:
      "Use standard launch ordering when no durable wearable path bias is available.",
    guidedStudio: {
      entryEmphasis: {
        eyebrow: "Standard entry",
        title: "Start from the standard guided flow",
        body: "No durable wearable path bias is available, so the system stays neutral and fully overridable.",
      },
      entryPriorityTokens: ["start", "guided", "studio"],
      workspaceBias: "standard",
      surfacePriorityLabel: "Standard surface priority",
      sortTokens: ["start", "guided", "studio"],
    },
    omniStudio: {
      entryEmphasis: {
        eyebrow: "Standard studio entry",
        title: "Use the standard OmniStudio launch order",
        body: "No durable wearable path bias is available, so studio entry remains neutral and fully overridable.",
      },
      workspaceBias: "standard",
      surfacePriorityLabel: "Standard studio ordering",
      emphasisTokens: ["studio", "scene", "home"],
      sortTokens: ["studio", "scene", "home"],
    },
  },
};

function normalizeTrack(value?: string | null): WearablePathTrack | null {
  if (!value) return null;
  const lowered = String(value).trim().toLowerCase();
  return VALID_TRACKS.includes(lowered as WearablePathTrack)
    ? (lowered as WearablePathTrack)
    : null;
}

function toSearchText(input: string): string {
  return input.trim().toLowerCase();
}

function scoreTextAgainstTokens(text: string, tokens: string[]): number {
  const normalized = toSearchText(text);
  let score = 0;

  for (const token of tokens) {
    if (!token) continue;
    if (normalized === token) {
      score += 6;
      continue;
    }
    if (normalized.includes(token)) {
      score += 3;
    }
  }

  return score;
}

function buildItemSearchText(item: PathAwareSortableItem): string {
  return [
    item.surfaceId ?? "",
    item.id ?? "",
    item.kind ?? "",
    item.title ?? "",
    ...(item.tags ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function resolveSortTokens(
  track: WearablePathTrack,
  mode: WearablePathSurfaceMode,
): string[] {
  return mode === "guided-studio"
    ? TRACK_CONFIGS[track].guidedStudio.sortTokens
    : TRACK_CONFIGS[track].omniStudio.sortTokens;
}

export function sortPathAwareIds(
  ids: string[],
  track: WearablePathTrack,
  mode: WearablePathSurfaceMode,
): string[] {
  const tokens = resolveSortTokens(track, mode);

  return [...ids]
    .map((id, index) => ({
      id,
      index,
      score: scoreTextAgainstTokens(id, tokens),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.index - b.index;
    })
    .map((entry) => entry.id);
}

export function sortPathAwareItems<T extends PathAwareSortableItem>(
  items: T[],
  track: WearablePathTrack,
  mode: WearablePathSurfaceMode,
): T[] {
  const tokens = resolveSortTokens(track, mode);

  return [...items]
    .map((item, index) => ({
      item,
      index,
      score: scoreTextAgainstTokens(buildItemSearchText(item), tokens),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.index - b.index;
    })
    .map((entry) => entry.item);
}

export function resolveWearablePathDefaultsForTrack(
  track: WearablePathTrack,
  source: WearablePathSource = "fallback",
  recommendedSurfaceIds: string[] = [],
): WearablePathDefaults {
  const config = TRACK_CONFIGS[track];

  return {
    track,
    source,
    isDurable: source === "bridge" || source === "baseline",
    continuityChips: [
      `Path: ${TRACK_LABELS[track]}`,
      source === "bridge" || source === "baseline"
        ? "Durable wearable bias"
        : source === "override-pinned" || source === "override-dismissed"
          ? "User entry preference"
          : "Continuity fallback",
      "Advisory defaults",
      "Override anytime",
    ],
    advisory: {
      summary: config.summary,
      sourceLabel: SOURCE_LABELS[source],
      overrideLabel:
        "These defaults guide entry only and do not change wearable truth.",
    },
    guidedStudio: {
      entryEmphasis: config.guidedStudio.entryEmphasis,
      entryPriorityTokens: config.guidedStudio.entryPriorityTokens,
      workspaceBias: config.guidedStudio.workspaceBias,
      surfacePriorityLabel: config.guidedStudio.surfacePriorityLabel,
    },
    omniStudio: {
      entryEmphasis: config.omniStudio.entryEmphasis,
      orderedSurfaceIds: sortPathAwareIds(
        recommendedSurfaceIds,
        track,
        "omnistudio",
      ),
      workspaceBias: config.omniStudio.workspaceBias,
      surfacePriorityLabel: config.omniStudio.surfacePriorityLabel,
      emphasisTokens: config.omniStudio.emphasisTokens,
    },
  };
}

export function resolveWearablePathDefaults(
  input: WearablePathDefaultsInput,
): WearablePathDefaults {
  const bridgeTrack = normalizeTrack(input.bridge?.preferredTrack);
  const baselineTrack = normalizeTrack(input.baseline?.preferredTrack);
  const onboardingTrack = normalizeTrack(input.onboardingTrack);

  let track: WearablePathTrack = "general";
  let source: WearablePathSource = "fallback";

  if (bridgeTrack) {
    track = bridgeTrack;
    source = "bridge";
  } else if (baselineTrack) {
    track = baselineTrack;
    source = "baseline";
  } else if (onboardingTrack) {
    track = onboardingTrack;
    source = "onboarding";
  }

  const recommendedSurfaceIds = Array.isArray(input.bridge?.recommendedSurfaceIds)
    ? input.bridge.recommendedSurfaceIds
    : [];

  return resolveWearablePathDefaultsForTrack(
    track,
    source,
    recommendedSurfaceIds,
  );
}